import DataTypes from "@/js/lookups/data-types.js";
import _ from "lodash";

function AccountFilters(fromCache) {
    this.fromCache = fromCache;

    this.children = [];

    this.methodStore = {};

    /**
     * Have all filters been resolved and translated?
     */
    this.initialized = false;

    let dataTypes = new DataTypes();

    this.DATA_TYPES = dataTypes.types;

    this.GENERIC_DATA_TYPES = dataTypes.genericTypes;

    this.lookupGenericDataType = function (dataTypeId) {
        let dataType = _.find(this.DATA_TYPES, { id: dataTypeId });
        return dataType ? dataType.genericTypeId : null;
    };

    /**
     * Many of the filters available to a user are dependent on the Akita account/customer.
     * - What integrations do they have?
     * - What properties does each integration have?
     * - What subscription level do they have?
     *
     * To address this, we can define the children of a filter as a function that returns a promise.
     *
     * Sometimes these promises must nest -- ex. What integrations? For each, whiich properties?
     *
     * This function 'resolves' these promises, recursively iterating through this object, resolving the promises
     * and replacing the function with the array that is returned by the function.
     */
    this.resolve = async function () {
        // Resolves array
        const promiseMap = (promiseList, functor) =>
            Promise.all(
                promiseList.map((promiseOrValue) =>
                    Promise.resolve(promiseOrValue).then(functor)
                )
            );

        // Resolves objects
        const promiseProps = (obj) =>
            Promise.all(
                Object.keys(obj).map((key) =>
                    Promise.resolve(obj[key]).then((val) => {
                        obj[key] = val;
                    })
                )
            ).then(() => obj);

        // Resolves recrusive deep objects
        const resolveNestedPromises = (obj) =>
            Promise.resolve(obj).then((obj) => {
                if (Array.isArray(obj)) {
                    return promiseMap(obj, (obj) => resolveNestedPromises(obj));
                } else if (obj && typeof obj === "object") {
                    // Object.assign(obj, obj.definition);
                    const obj2 = {};
                    for (const key in obj) {
                        obj2[key] = resolveNestedPromises(obj[key]);
                    }
                    return promiseProps(obj2);
                }
                return obj;
            });

        /**
         * Recursively resolve any promises in the this object
         */
        return await resolveNestedPromises(this);
    };

    this.translate = function ($i18n) {
        let prefix = "entityFilters.types";
        const traverse = function (obj) {
            for (var k in obj) {
                if (typeof obj[k] === "object" && obj[k] !== null) {
                    traverse(obj[k]);
                } else if (
                    /**
                     * label is a function [ call it ]
                     */
                    obj.hasOwnProperty(k) &&
                    typeof obj[k] == "function" &&
                    k == "label"
                ) {
                    obj["label"] = obj["label"]();
                } else if (
                    /**
                     * label is NOT a function use as translation
                     */
                    obj.hasOwnProperty(k) &&
                    typeof obj[k] != "function" &&
                    k == "label"
                ) {
                    obj["label"] = $i18n.t(prefix + "." + obj[k]) || obj[k];
                }
            }
        };
        traverse(this);
    };

    this.lookupByKey = function (haystack, needle, token) {
        var token = token || "key";
        const search = function (items, key) {
            let item;
            for (var index in items) {
                item = items[index];
                if (key == _.get(item, "definition." + token)) {
                    return item;
                }
                if (item.children) {
                    let result = search(item.children, key);
                    if (result) {
                        return result;
                    }
                }
            }
        };
        return search(haystack, needle);
    };

    /**
     * This method takes an object that has been deserialized from JSON and appends
     * methods (which cannot be seriealized with JSON)
     *
     * Things like remote() and validate()
     *
     * @param {*} childrenFromCache
     * @param {*} methods
     * @returns array of processed children
     */
    this.decorate = function (childrenFromCache, methods) {
        const traverse = function (obj) {
            for (var k in obj) {
                if (
                    typeof obj[k] === "object" &&
                    obj.hasOwnProperty(k) &&
                    k == "decorate"
                ) {
                    for (const [key, value] of Object.entries(obj[k])) {
                        obj[value.method] = methods[value.closure];
                    }
                } else if (typeof obj[k] === "object" && obj[k] !== null) {
                    traverse(obj[k]);
                }
            }
        };
        traverse(childrenFromCache);
        return childrenFromCache;
    };

    /**
     * @param {*} raw ...from this file
     * @param {*} fromCache
     *
     * We need the "decorate" properties
     */
    this.addDecorateDefinition = function (raw, fromCache) {
        let _this = this;
        let rawItem;
        const traverse = function (obj) {
            for (var k in obj) {
                if (typeof obj[k] === "object" && obj.hasOwnProperty(k)) {
                    for (const [key, value] of Object.entries(obj[k])) {
                        if ("definition" == key) {
                            rawItem = _.cloneDeep(
                                _this.lookupByKey(raw, value.key)
                            );
                            Object.assign(
                                obj[k]["definition"],
                                _.pick(rawItem["definition"], ["decorate"])
                            );
                        }
                    }
                } else if (typeof obj[k] === "object" && obj[k] !== null) {
                    traverse(obj[k]);
                }
            }
        };
        traverse(fromCache);
        return fromCache;
    };

    this.init = async function ($i18n, $store) {
        var _this = this;

        this.label = "root";
        this.children = [
            {
                definition: Object.assign({
                    key: "accountName",
                    label: "accountName",
                    type: "genericText",
                    parameters: {
                        property: "orgs__name",
                    },
                    tests: ["textEquals", "textContains"],
                }),
                children: [],
            },
            {
                definition: Object.assign({
                    key: "accountCreatedAt",
                    label: "accountCreatedAt",
                    type: "genericDate",
                    parameters: {
                        property: "orgs__created_at",
                    },
                    genericDataTypeId: _this.GENERIC_DATA_TYPES.DATETIME.id,
                    tests: [
                        "isNull",
                        "isNotNull",
                        "dateEquals",
                        "dateAfterAbsolute",
                        "dateBeforeAbsolute",
                        "dateAfterPast",
                        "dateBeforePast",
                        "dateAfterFuture",
                        "dateBeforeFuture",
                    ],
                }),
                children: [],
            },
            {
                definition: Object.assign({
                    label: "status",
                }),
                children: [
                    {
                        definition: Object.assign({
                            key: "isActive",
                            label: "isActive",
                            type: "genericBoolean",
                            genericDataTypeId:
                                _this.GENERIC_DATA_TYPES.BOOLEAN.id,
                            parameters: {
                                property: "orgs__is_active",
                            },
                            tests: [
                                "booleanTrue",
                                "booleanFalse",
                                "booleanNotTrue",
                                "booleanNotFalse",
                                "isNull",
                                "isNotNull",
                            ],
                        }),
                        children: [],
                    },
                    {
                        definition: Object.assign({
                            key: "isChurned",
                            label: "isChurned",
                            type: "genericBoolean",
                            genericDataTypeId:
                                _this.GENERIC_DATA_TYPES.BOOLEAN.id,
                            parameters: {
                                property: "orgs__is_churned",
                            },
                            tests: [
                                "booleanTrue",
                                "booleanFalse",
                                "booleanNotTrue",
                                "booleanNotFalse",
                                "isNull",
                                "isNotNull",
                            ],
                        }),
                        children: [],
                    },
                ],
            },
            {
                definition: Object.assign({
                    label: "financials",
                }),
                children: [
                    {
                        definition: Object.assign({
                            label: $store.getters["usPreferences/VALUE_UNIT"],
                        }),
                        children: [
                            {
                                definition: Object.assign({
                                    key: "value",
                                    label: "value",
                                    type: "genericCurrency",
                                    genericDataTypeId:
                                        _this.GENERIC_DATA_TYPES.CURRENCY.id,
                                    label: $store.getters[
                                        "usPreferences/VALUE_UNIT"
                                    ],
                                    parameters: {
                                        property: "orgs__value",
                                    },
                                    tests: [
                                        "isNull",
                                        "isNotNull",
                                        "numberGreaterThan",
                                        "numberGreaterThanOrEqualTo",
                                        "numberLessThan",
                                        "numberLessThanOrEqualTo",
                                        "numberBetween",
                                        "numberEquals",
                                    ],
                                }),
                                children: [],
                            },
                            {
                                definition: Object.assign({
                                    key: "valueDelta",
                                    type: "statisticDelta",
                                    genericDataTypeId:
                                        _this.GENERIC_DATA_TYPES.CURRENCY.id,
                                    parameters: {
                                        typeId: 4,
                                        relatedId: "value",
                                    },
                                    label: () => {
                                        return (
                                            "Change in " +
                                            $store.getters[
                                                "usPreferences/VALUE_UNIT"
                                            ] +
                                            " Over Time"
                                        );
                                    },
                                    tests: [
                                        "numberIncreasedGreaterThanOrEqualTo",
                                        "numberIncreasedGreaterThan",
                                        "numberIncreasedLessThanOrEqualTo",
                                        "numberIncreasedLessThan",
                                        "numberDecreasedGreaterThanOrEqualTo",
                                        "numberDecreasedGreaterThan",
                                        "numberDecreasedLessThanOrEqualTo",
                                        "numberDecreasedLessThan",
                                        "numberChangedBetween",
                                    ],
                                }),
                                children: [],
                            },
                        ],
                    },
                    {
                        definition: Object.assign({
                            key: "currency",
                            label: "currency",
                            type: "genericText",
                            genericDataTypeId:
                                _this.GENERIC_DATA_TYPES.STRING.id,
                            parameters: {
                                property: "orgs__currency",
                            },
                            tests: [
                                "isNull",
                                "isNotNull",
                                "textEquals",
                                "textNotEquals",
                            ],
                        }),
                        children: [],
                    },
                    {
                        definition: Object.assign({
                            key: "customerStartDate",
                            label: "customerStartDate",
                            type: "genericDate",
                            genericDataTypeId:
                                _this.GENERIC_DATA_TYPES.DATETIME.id,
                            parameters: {
                                property: "orgs__customer_start_date",
                            },
                            tests: [
                                "isNull",
                                "isNotNull",
                                "dateEquals",
                                "dateAfterAbsolute",
                                "dateBeforeAbsolute",
                                "dateAfterPast",
                                "dateBeforePast",
                                "dateAfterFuture",
                                "dateBeforeFuture",
                            ],
                        }),
                        children: [],
                    },
                    {
                        definition: Object.assign({
                            key: "customerEndDate",
                            label: "customerEndDate",
                            type: "genericDate",
                            genericDataTypeId:
                                _this.GENERIC_DATA_TYPES.DATETIME.id,
                            parameters: {
                                property: "orgs__customer_end_date",
                            },
                            tests: [
                                "isNull",
                                "isNotNull",
                                "dateEquals",
                                "dateAfterAbsolute",
                                "dateBeforeAbsolute",
                                "dateAfterPast",
                                "dateBeforePast",
                                "dateAfterFuture",
                                "dateBeforeFuture",
                            ],
                        }),
                        children: [],
                    },
                    {
                        definition: Object.assign({
                            key: "contractStartDate",
                            label: "contractStartDate",
                            type: "genericDate",
                            genericDataTypeId:
                                _this.GENERIC_DATA_TYPES.DATETIME.id,
                            parameters: {
                                property: "orgs__contract_start_date",
                            },
                            tests: [
                                "isNull",
                                "isNotNull",
                                "dateEquals",
                                "dateAfterAbsolute",
                                "dateBeforeAbsolute",
                                "dateAfterPast",
                                "dateBeforePast",
                                "dateAfterFuture",
                                "dateBeforeFuture",
                            ],
                        }),
                        children: [],
                    },
                    {
                        definition: Object.assign({
                            key: "contractEndDate",
                            label: "contractEndDate",
                            type: "genericDate",
                            genericDataTypeId:
                                _this.GENERIC_DATA_TYPES.DATETIME.id,
                            parameters: {
                                property: "orgs__contract_end_date",
                            },
                            tests: [
                                "isNull",
                                "isNotNull",
                                "dateEquals",
                                "dateAfterAbsolute",
                                "dateBeforeAbsolute",
                                "dateAfterPast",
                                "dateBeforePast",
                                "dateAfterFuture",
                                "dateBeforeFuture",
                            ],
                        }),
                        children: [],
                    },
                    {
                        definition: Object.assign({
                            key: "activeLicenseCount",
                            label: "activeLicenseCount",
                            genericDataTypeId:
                                _this.GENERIC_DATA_TYPES.NUMBER.id,
                            parameters: {
                                property: "orgs__active_license_count",
                            },
                            tests: [
                                "isNull",
                                "isNotNull",
                                "numberGreaterThan",
                                "numberGreaterThanOrEqualTo",
                                "numberLessThan",
                                "numberLessThanOrEqualTo",
                                "numberBetween",
                                "numberEquals",
                            ],
                        }),
                        children: [],
                    },
                ],
            },
            {
                definition: Object.assign({
                    label: "surveys",
                }),
                children: [
                    {
                        definition: Object.assign({
                            label: "nps",
                        }),
                        children: [
                            {
                                definition: Object.assign({
                                    key: "nps",
                                    label: "nps",
                                    type: "genericNumeric",
                                    genericDataTypeId:
                                        _this.GENERIC_DATA_TYPES.NUMBER.id,
                                    parameters: {
                                        property: "orgs__nps",
                                    },
                                    tests: [
                                        "isNull",
                                        "isNotNull",
                                        "numberGreaterThan",
                                        "numberGreaterThanOrEqualTo",
                                        "numberLessThan",
                                        "numberLessThanOrEqualTo",
                                        "numberBetween",
                                        "numberEquals",
                                    ],
                                }),
                                children: [],
                            },
                            {
                                definition: Object.assign({
                                    key: "npsDelta",
                                    label: "npsDelta",
                                    type: "statisticDelta",
                                    parameters: {
                                        typeId: 4,
                                        relatedId: "nps",
                                    },
                                    tests: [
                                        "numberIncreasedGreaterThanOrEqualTo",
                                        "numberIncreasedGreaterThan",
                                        "numberIncreasedLessThanOrEqualTo",
                                        "numberIncreasedLessThan",
                                        "numberDecreasedGreaterThanOrEqualTo",
                                        "numberDecreasedGreaterThan",
                                        "numberDecreasedLessThanOrEqualTo",
                                        "numberDecreasedLessThan",
                                        "numberChangedBetween",
                                    ],
                                }),
                                children: [],
                            },
                        ],
                    },
                    {
                        definition: Object.assign({
                            key: "csats",
                            label: "csats",
                            type: "genericNumeric",
                            genericDataTypeId:
                                _this.GENERIC_DATA_TYPES.NUMBER.id,
                            parameters: {
                                property: "orgs__csats",
                            },
                            tests: [
                                "isNull",
                                "isNotNull",
                                "numberGreaterThan",
                                "numberGreaterThanOrEqualTo",
                                "numberLessThan",
                                "numberLessThanOrEqualTo",
                                "numberBetween",
                                "numberEquals",
                            ],
                        }),
                        children: [],
                    },
                ],
            },
            {
                definition: Object.assign({
                    label: "health",
                }),
                children: [
                    {
                        definition: Object.assign({
                            label: "csmScore",
                        }),
                        children: [
                            {
                                definition: Object.assign({
                                    key: "csmScore",
                                    label: "csmScore",
                                    type: "genericNumeric",
                                    genericDataTypeId:
                                        _this.GENERIC_DATA_TYPES.NUMBER.id,
                                    parameters: {
                                        property: "orgs__csm_score",
                                    },
                                    tests: [
                                        "isNull",
                                        "isNotNull",
                                        "numberGreaterThan",
                                        "numberGreaterThanOrEqualTo",
                                        "numberLessThan",
                                        "numberLessThanOrEqualTo",
                                        "numberBetween",
                                        "numberPicklist",
                                    ],
                                    options: [
                                        {
                                            value: 1,
                                            option: "1 - Poor",
                                            html: "<span class='ivu-badge-status ivu-badge'><span class='ivu-badge-status-dot ivu-badge-status-error'></span> <span class='ivu-badge-status-text'>1 - Poor</span></span>",
                                        },
                                        {
                                            value: 2,
                                            option: "2 - Fair",
                                            html: "<span class='ivu-badge-status ivu-badge'><span class='ivu-badge-status-dot ivu-badge-status-warning'></span> <span class='ivu-badge-status-text'>2 - Fair</span></span>",
                                        },
                                        {
                                            value: 3,
                                            option: "3 - Good",
                                            html: "<span class='ivu-badge-status ivu-badge'><span class='ivu-badge-status-dot ivu-badge-status-success'></span> <span class='ivu-badge-status-text'>3 - Good</span></span>",
                                        },
                                    ],
                                }),
                                children: [],
                            },
                            {
                                definition: Object.assign({
                                    key: "csmScoreLastUpdatedAt",
                                    label: "csmScoreLastUpdatedAt",
                                    type: "genericDate",
                                    genericDataTypeId:
                                        _this.GENERIC_DATA_TYPES.DATETIME.id,
                                    parameters: {
                                        property:
                                            "orgs__csm_score_last_updated_at",
                                    },
                                    tests: [
                                        "isNull",
                                        "isNotNull",
                                        "dateEquals",
                                        "dateAfterAbsolute",
                                        "dateBeforeAbsolute",
                                        "dateAfterPast",
                                        "dateBeforePast",
                                        "dateAfterFuture",
                                        "dateBeforeFuture",
                                    ],
                                }),
                                children: [],
                            },
                        ],
                    },
                    {
                        definition: Object.assign({
                            label: "healthScore",
                        }),
                        children: [
                            {
                                definition: Object.assign({
                                    key: "healthScore",
                                    label: "healthScore",
                                    type: "genericNumeric",
                                    genericDataTypeId:
                                        _this.GENERIC_DATA_TYPES.NUMBER.id,
                                    parameters: {
                                        property: "orgs__score",
                                    },
                                    tests: [
                                        "isNull",
                                        "isNotNull",
                                        "numberGreaterThan",
                                        "numberGreaterThanOrEqualTo",
                                        "numberLessThan",
                                        "numberLessThanOrEqualTo",
                                        "numberBetween",
                                        "numberEquals",
                                    ],
                                }),
                                children: [],
                            },
                            {
                                definition: Object.assign({
                                    key: "healthScoreDelta",
                                    label: "healthScoreDelta",
                                    type: "statisticDelta",
                                    parameters: {
                                        typeId: 4,
                                        relatedId: "score",
                                    },
                                    tests: [
                                        "numberIncreasedGreaterThanOrEqualTo",
                                        "numberIncreasedGreaterThan",
                                        "numberIncreasedLessThanOrEqualTo",
                                        "numberIncreasedLessThan",
                                        "numberDecreasedGreaterThanOrEqualTo",
                                        "numberDecreasedGreaterThan",
                                        "numberDecreasedLessThanOrEqualTo",
                                        "numberDecreasedLessThan",
                                        "numberChangedBetween",
                                    ],
                                }),
                                children: [],
                            },
                            {
                                definition: Object.assign({
                                    key: "healthScorePercentage",
                                    label: "healthScorePercentage",
                                    type: "genericNumeric",
                                    genericDataTypeId:
                                        _this.GENERIC_DATA_TYPES.NUMBER.id,
                                    parameters: {
                                        property: "orgs__score_percentile",
                                    },
                                    tests: [
                                        "isNull",
                                        "isNotNull",
                                        "numberGreaterThan",
                                        "numberGreaterThanOrEqualTo",
                                        "numberLessThan",
                                        "numberLessThanOrEqualTo",
                                        "numberBetween",
                                        "numberEquals",
                                    ],
                                }),
                                children: [],
                            },
                        ],
                    },
                    {
                        definition: Object.assign({
                            label: "healthScoresCustom",
                        }),
                        children: axios
                            .get(window.akita.urls.api + "/v1/segments", {
                                params: {
                                    per_page: 100,
                                    q: ["type_id[eq]=3", "status_id[eq]=1"],
                                },
                            })
                            .then(function (response) {
                                var items = [];
                                response.data.data.map((segment) => {
                                    items.push({
                                        definition: Object.assign({
                                            key: "score__" + segment.id,
                                            type: "genericNumeric",
                                            dataTypeId:
                                                _this.GENERIC_DATA_TYPES.NUMBER
                                                    .id,
                                            label: () => {
                                                return segment.name;
                                            },
                                            parameters: {
                                                property:
                                                    "orgs__" +
                                                    "score_" +
                                                    segment.id +
                                                    "__score",
                                            },
                                            tests: [
                                                "isNull",
                                                "isNotNull",
                                                "numberGreaterThan",
                                                "numberGreaterThanOrEqualTo",
                                                "numberLessThan",
                                                "numberLessThanOrEqualTo",
                                                "numberBetween",
                                                "numberEquals",
                                            ],
                                        }),
                                        children: [],
                                    });
                                    items.push({
                                        definition: Object.assign({
                                            key: "scoreDelta__" + segment.id,
                                            type: "statisticDelta",
                                            dataTypeId:
                                                _this.GENERIC_DATA_TYPES.NUMBER
                                                    .id,
                                            label: () => {
                                                return (
                                                    "Change in " +
                                                    segment.name +
                                                    " Over Time"
                                                );
                                            },
                                            parameters: {
                                                typeId: 4,
                                                relatedId: "score",
                                            },
                                            tests: [
                                                "numberIncreasedGreaterThanOrEqualTo",
                                                "numberIncreasedGreaterThan",
                                                "numberIncreasedLessThanOrEqualTo",
                                                "numberIncreasedLessThan",
                                                "numberDecreasedGreaterThanOrEqualTo",
                                                "numberDecreasedGreaterThan",
                                                "numberDecreasedLessThanOrEqualTo",
                                                "numberDecreasedLessThan",
                                                "numberChangedBetween",
                                            ],
                                        }),
                                        children: [],
                                    });
                                    items.push({
                                        definition: Object.assign({
                                            key:
                                                "scorePercentage__" +
                                                segment.id,
                                            type: "genericPercentage",
                                            dataTypeId:
                                                _this.GENERIC_DATA_TYPES.NUMBER
                                                    .id,
                                            label: () => {
                                                return (
                                                    segment.name + ": % Rank"
                                                );
                                            },
                                            parameters: {
                                                property:
                                                    "orgs__" +
                                                    "score_" +
                                                    segment.id +
                                                    "__score_percentile",
                                            },
                                            tests: [
                                                "isNull",
                                                "isNotNull",
                                                "numberGreaterThan",
                                                "numberGreaterThanOrEqualTo",
                                                "numberLessThan",
                                                "numberLessThanOrEqualTo",
                                                "numberBetween",
                                                "numberEquals",
                                            ],
                                        }),
                                        children: [],
                                    });
                                });
                                return items;
                            }),
                    },
                ],
            },
            {
                definition: Object.assign({
                    label: "assignedUsers",
                }),
                children: axios
                    .get(window.akita.urls.api + "/v1/roles", {
                        params: {
                            per_page: 1000,
                            q: ["can_own_accounts[eq]=true"],
                        },
                    })
                    .then(function (response) {
                        var items = [];
                        response.data.data.map((role) => {
                            items.push({
                                definition: Object.assign({
                                    key: "role_" + role.id + "__user_id",
                                    type: "genericNumeric",
                                    dataTypeId:
                                        _this.GENERIC_DATA_TYPES.NUMBER.id,
                                    label: () => {
                                        return role.display_name;
                                    },
                                    parameters: {
                                        property:
                                            "orgs__" +
                                            "role_" +
                                            role.id +
                                            "__user_id",
                                    },
                                    tests: [
                                        "isNull",
                                        "isNotNull",
                                        "numberPicklist",
                                    ],
                                    options: axios
                                        .get(
                                            window.akita.urls.api + "/v1/users",
                                            {
                                                params: {
                                                    per_page: 1000,
                                                    per_page: 100,
                                                },
                                            }
                                        )
                                        .then(function (response) {
                                            var items = [];
                                            response.data.data.map((item) => {
                                                items.push({
                                                    value: item.id,
                                                    option:
                                                        item.last_name +
                                                        ", " +
                                                        item.first_name,
                                                });
                                            });
                                            return items;
                                        }),
                                }),
                                children: [],
                            });
                        });
                        return items;
                    }),
            },
            {
                definition: Object.assign({
                    label: "usageMetrics",
                }),
                children: [
                    {
                        definition: Object.assign({
                            label: "webSessions",
                        }),
                        children: [
                            {
                                definition: Object.assign({
                                    key: "webSessionsCount",
                                    label: "webSessionsCount",
                                    type: "webSessionsCount",
                                    dataTypeId:
                                        _this.GENERIC_DATA_TYPES.NUMBER.id,
                                    tests: [
                                        "numberGreaterThanInTimeFrame",
                                        "numberGreaterThanOrEqualToInTimeFrame",
                                        "numberLessThanInTimeFrame",
                                        "numberLessThanOrEqualToInTimeFrame",
                                        "numberBetweenInTimeFrame",
                                        "numberEqualsInTimeFrame",
                                    ],
                                }),
                                children: [],
                            },
                            {
                                definition: Object.assign({
                                    key: "webSessionsDelta",
                                    label: "webSessionsDelta",
                                    type: "webSessionsDelta",
                                    tests: [
                                        "numberIncreasedGreaterThanOrEqualTo",
                                        "numberIncreasedGreaterThan",
                                        "numberIncreasedLessThanOrEqualTo",
                                        "numberIncreasedLessThan",
                                        "numberDecreasedGreaterThanOrEqualTo",
                                        "numberDecreasedGreaterThan",
                                        "numberDecreasedLessThanOrEqualTo",
                                        "numberDecreasedLessThan",
                                        "numberChangedBetween",
                                    ],
                                }),
                                children: [],
                            },
                        ],
                    },
                    {
                        definition: Object.assign({
                            label: "events",
                        }),
                        children: [
                            {
                                definition: Object.assign({
                                    key: "eventsCount",
                                    label: "eventsCount",
                                    type: "eventsCount",
                                    dataTypeId:
                                        _this.GENERIC_DATA_TYPES.NUMBER.id,
                                    labelProperty: "event",
                                    tests: [
                                        "numberGreaterThanInTimeFrame",
                                        "numberGreaterThanOrEqualToInTimeFrame",
                                        "numberLessThanInTimeFrame",
                                        "numberLessThanOrEqualToInTimeFrame",
                                        "numberBetweenInTimeFrame",
                                        "numberEqualsInTimeFrame",
                                    ],
                                    properties: [
                                        {
                                            name: "event",
                                            label: () => {
                                                return $i18n.t(
                                                    "entityFilters.properties.eventName"
                                                );
                                            },
                                            type: "autoComplete",
                                            decorate: [
                                                {
                                                    method: "remote",
                                                    closure: "event__remote",
                                                },
                                                {
                                                    method: "validate",
                                                    closure: "event__validate",
                                                },
                                            ],
                                        },
                                    ],
                                }),
                                children: [],
                            },
                            {
                                definition: Object.assign({
                                    key: "eventsDelta",
                                    label: "eventsDelta",
                                    type: "eventsDelta",
                                    tests: [
                                        "numberIncreasedGreaterThanOrEqualTo",
                                        "numberIncreasedGreaterThan",
                                        "numberIncreasedLessThanOrEqualTo",
                                        "numberIncreasedLessThan",
                                        "numberDecreasedGreaterThanOrEqualTo",
                                        "numberDecreasedGreaterThan",
                                        "numberDecreasedLessThanOrEqualTo",
                                        "numberDecreasedLessThan",
                                        "numberChangedBetween",
                                    ],
                                    properties: [
                                        {
                                            name: "event",
                                            label: () => {
                                                return $i18n.t(
                                                    "entityFilters.properties.eventName"
                                                );
                                            },
                                            type: "autoComplete",
                                            decorate: [
                                                {
                                                    method: "remote",
                                                    closure: "event__remote",
                                                },
                                                {
                                                    method: "validate",
                                                    closure: "event__validate",
                                                },
                                            ],
                                        },
                                    ],
                                }),
                                children: [],
                            },
                        ],
                    },
                    {
                        definition: Object.assign({
                            label: "dau",
                        }),
                        children: [
                            {
                                definition: Object.assign({
                                    key: "dau",
                                    label: "dau",
                                    type: "genericNumeric",
                                    dataTypeId:
                                        _this.GENERIC_DATA_TYPES.NUMBER.id,
                                    parameters: {
                                        property: "orgs____active_users__dau",
                                    },
                                    tests: [
                                        "numberGreaterThan",
                                        "numberGreaterThanOrEqualTo",
                                        "numberLessThan",
                                        "numberLessThanOrEqualTo",
                                        "numberBetween",
                                        "numberEquals",
                                    ],
                                }),
                                children: [],
                            },
                            {
                                definition: Object.assign({
                                    key: "dauPercentage",
                                    label: "dauPercentage",
                                    type: "genericPercentage",
                                    dataTypeId:
                                        _this.GENERIC_DATA_TYPES.NUMBER.id,
                                    parameters: {
                                        property:
                                            "orgs____active_users__dau_percentage",
                                    },
                                    tests: [
                                        "numberGreaterThan",
                                        "numberGreaterThanOrEqualTo",
                                        "numberLessThan",
                                        "numberLessThanOrEqualTo",
                                        "numberBetween",
                                        "numberEquals",
                                    ],
                                }),
                                children: [],
                            },
                        ],
                    },
                    {
                        definition: Object.assign({
                            label: "mau",
                        }),
                        children: [
                            {
                                definition: Object.assign({
                                    key: "mau",
                                    label: "mau",
                                    type: "genericNumeric",
                                    dataTypeId:
                                        _this.GENERIC_DATA_TYPES.NUMBER.id,
                                    parameters: {
                                        property: "orgs____active_users__mau",
                                    },
                                    tests: [
                                        "numberGreaterThan",
                                        "numberGreaterThanOrEqualTo",
                                        "numberLessThan",
                                        "numberLessThanOrEqualTo",
                                        "numberBetween",
                                        "numberEquals",
                                    ],
                                }),
                                children: [],
                            },
                            {
                                definition: Object.assign({
                                    key: "mauPercentage",
                                    label: "mauPercentage",
                                    type: "genericPercentage",
                                    dataTypeId:
                                        _this.GENERIC_DATA_TYPES.NUMBER.id,
                                    parameters: {
                                        property:
                                            "orgs____active_users__mau_percentage",
                                    },
                                    tests: [
                                        "numberGreaterThan",
                                        "numberGreaterThanOrEqualTo",
                                        "numberLessThan",
                                        "numberLessThanOrEqualTo",
                                        "numberBetween",
                                        "numberEquals",
                                    ],
                                }),
                                children: [],
                            },
                        ],
                    },
                    {
                        definition: Object.assign({
                            label: "pageViews",
                        }),
                        children: [
                            {
                                definition: Object.assign({
                                    key: "pageViewsCountName",
                                    label: "pageViewsCountName",
                                    type: "pageViewsCountName",
                                    dataTypeId:
                                        _this.GENERIC_DATA_TYPES.STRING.id,
                                    labelProperty: "pageName",
                                    tests: [
                                        "numberGreaterThanInTimeFrame",
                                        "numberGreaterThanOrEqualToInTimeFrame",
                                        "numberLessThanInTimeFrame",
                                        "numberLessThanOrEqualToInTimeFrame",
                                        "numberBetweenInTimeFrame",
                                        "numberEqualsInTimeFrame",
                                    ],
                                    properties: [
                                        {
                                            name: "pageName",
                                            label: () => {
                                                return $i18n.t(
                                                    "entityFilters.properties.pageViewsPageName"
                                                );
                                            },
                                            type: "autoComplete",
                                            decorate: [
                                                {
                                                    method: "remote",
                                                    closure: "pageName__remote",
                                                },
                                                {
                                                    method: "validate",
                                                    closure:
                                                        "pageName__validate",
                                                },
                                            ],
                                        },
                                    ],
                                }),
                                children: [],
                            },
                            {
                                definition: Object.assign({
                                    key: "pageViewsCountPath",
                                    label: "pageViewsCountPath",
                                    type: "pageViewsCountPath",
                                    dataTypeId:
                                        _this.GENERIC_DATA_TYPES.STRING.id,
                                    labelProperty: "pagePath",
                                    tests: [
                                        "numberGreaterThanInTimeFrame",
                                        "numberGreaterThanOrEqualToInTimeFrame",
                                        "numberLessThanInTimeFrame",
                                        "numberLessThanOrEqualToInTimeFrame",
                                        "numberBetweenInTimeFrame",
                                        "numberEqualsInTimeFrame",
                                    ],
                                    properties: [
                                        {
                                            name: "pagePath",
                                            label: () => {
                                                return $i18n.t(
                                                    "entityFilters.properties.pageViewsPagePath"
                                                );
                                            },
                                            type: "autoComplete",
                                            decorate: [
                                                {
                                                    method: "remote",
                                                    closure: "pagePath__remote",
                                                },
                                                {
                                                    method: "validate",
                                                    closure:
                                                        "pagePath__validate",
                                                },
                                            ],
                                        },
                                    ],
                                }),
                                children: [],
                            },
                        ],
                    },
                    {
                        definition: Object.assign({
                            key: "stickiness",
                            label: "stickiness",
                            type: "genericPercentage",
                            genericDataTypeId:
                                _this.GENERIC_DATA_TYPES.NUMBER.id,
                            parameters: {
                                property: "orgs____active_users__stickiness",
                            },
                            tests: [
                                "numberGreaterThan",
                                "numberGreaterThanOrEqualTo",
                                "numberLessThan",
                                "numberLessThanOrEqualTo",
                                "numberBetween",
                                "numberEquals",
                            ],
                        }),
                        children: [],
                    },
                ],
            },
            {
                definition: Object.assign({
                    label: "traitsCustomFields",
                }),
                children: axios
                    .get(window.akita.urls.api + "/v1/integration-traits", {
                        params: {
                            per_page: 1000,
                            q: [
                                "provider_id[eq]=134",
                                "interaction_type_id[eq]=1",
                            ],
                        },
                    })
                    .then(function (response) {
                        var items = [];
                        response.data.data.map((integrationTrait) => {
                            items.push({
                                definition: Object.assign({
                                    key: integrationTrait.segment_identifier,
                                    type: "generic",
                                    genericDataTypeId:
                                        _this.lookupGenericDataType(
                                            integrationTrait.data_type_id
                                        ),
                                    label: () => {
                                        return (
                                            integrationTrait.label ||
                                            integrationTrait.attribute
                                        );
                                    },
                                    parameters: {
                                        property:
                                            "orgs__" +
                                            integrationTrait.segment_identifier,
                                    },
                                    tests: [],
                                }),
                                children: [],
                            });
                        });
                        return items;
                    }),
            },
            {
                definition: Object.assign({
                    label: "traitsUserProvided",
                }),
                children: axios
                    .get(window.akita.urls.api + "/v1/integration-traits", {
                        params: {
                            per_page: 1000,
                            q: [
                                "provider_id[eq]=105",
                                "interaction_type_id[eq]=1",
                            ],
                        },
                    })
                    .then(function (response) {
                        var items = [];
                        response.data.data.map((integrationTrait) => {
                            items.push({
                                definition: Object.assign({
                                    key: integrationTrait.segment_identifier,
                                    type: "generic",
                                    genericDataTypeId:
                                        _this.lookupGenericDataType(
                                            integrationTrait.data_type_id
                                        ),
                                    label: () => {
                                        return (
                                            integrationTrait.label ||
                                            integrationTrait.attribute
                                        );
                                    },
                                    parameters: {
                                        property:
                                            "orgs__" +
                                            integrationTrait.segment_identifier,
                                    },
                                    tests: [],
                                }),
                                children: [],
                            });
                        });
                        return items;
                    }),
            },
            {
                definition: Object.assign({
                    label: "traitsIntegrations",
                }),
                children: axios
                    .get(window.akita.urls.api + "/v1/integrations", {
                        params: {
                            per_page: 100,
                            q: ["provider_id[notin]=105,131,134"],
                            include: ["provider"],
                            traits: "true",
                        },
                    })
                    .then((response) => {
                        var items = [];
                        response.data.data.map((integration) => {
                            items.push({
                                definition: Object.assign({
                                    label: () => {
                                        return (
                                            integration.label ||
                                            integration.provider.name
                                        );
                                    },
                                }),
                                children: axios
                                    .get(
                                        window.akita.urls.api +
                                            "/v1/integration-traits",
                                        {
                                            params: {
                                                per_page: 1000,
                                                q: [
                                                    "interaction_type_id[eq]=1",
                                                    "provider_id[eq]=" +
                                                        integration.provider_id,
                                                ],
                                            },
                                        }
                                    )
                                    .then(function (response) {
                                        var items = [];
                                        response.data.data.map(
                                            (integrationTrait) => {
                                                items.push({
                                                    definition: Object.assign({
                                                        key: integrationTrait.segment_identifier,
                                                        type: "generic",
                                                        genericDataTypeId:
                                                            _this.lookupGenericDataType(
                                                                integrationTrait.data_type_id
                                                            ),
                                                        label: () => {
                                                            return (
                                                                integrationTrait.label ||
                                                                integrationTrait.attribute
                                                            );
                                                        },
                                                        parameters: {
                                                            property:
                                                                "orgs__" +
                                                                integrationTrait.segment_identifier,
                                                        },
                                                        tests: [],
                                                    }),
                                                    children: [],
                                                });
                                            }
                                        );
                                        return items;
                                    }),
                            });
                        });
                        return items;
                    }),
            },
            {
                definition: Object.assign({
                    label: "interactions",
                }),
                children: [
                    {
                        definition: Object.assign({
                            label: "interactionDates",
                        }),
                        children: [
                            {
                                definition: Object.assign({
                                    label: "interactionsDatesEmailThreads",
                                }),
                                children: [
                                    {
                                        definition: Object.assign({
                                            key: "interactionsDatesEmailThreadsFirstSent",
                                            label: "interactionsDatesEmailThreadsFirstSent",
                                            type: "genericDate",
                                            parameters: {
                                                property:
                                                    "orgs____email_thread_dates_by_organization__date_first_email_sent",
                                            },
                                            genericDataTypeId:
                                                _this.GENERIC_DATA_TYPES
                                                    .DATETIME.id,
                                            tests: [
                                                "isNull",
                                                "isNotNull",
                                                "dateEquals",
                                                "dateAfterAbsolute",
                                                "dateBeforeAbsolute",
                                                "dateAfterPast",
                                                "dateBeforePast",
                                                "dateAfterFuture",
                                                "dateBeforeFuture",
                                            ],
                                        }),
                                        children: [],
                                    },
                                    {
                                        definition: Object.assign({
                                            key: "interactionsDatesEmailThreadsLastSent",
                                            label: "interactionsDatesEmailThreadsLastSent",
                                            type: "genericDate",
                                            parameters: {
                                                property:
                                                    "orgs____email_thread_dates_by_organization__date_last_email_sent",
                                            },
                                            genericDataTypeId:
                                                _this.GENERIC_DATA_TYPES
                                                    .DATETIME.id,
                                            tests: [
                                                "isNull",
                                                "isNotNull",
                                                "dateEquals",
                                                "dateAfterAbsolute",
                                                "dateBeforeAbsolute",
                                                "dateAfterPast",
                                                "dateBeforePast",
                                                "dateAfterFuture",
                                                "dateBeforeFuture",
                                            ],
                                        }),
                                        children: [],
                                    },
                                    {
                                        definition: Object.assign({
                                            key: "interactionsDatesEmailThreadsFirstReceived",
                                            label: "interactionsDatesEmailThreadsFirstReceived",
                                            type: "genericDate",
                                            parameters: {
                                                property:
                                                    "orgs____email_thread_dates_by_organization__date_first_email_received",
                                            },
                                            genericDataTypeId:
                                                _this.GENERIC_DATA_TYPES
                                                    .DATETIME.id,
                                            tests: [
                                                "isNull",
                                                "isNotNull",
                                                "dateEquals",
                                                "dateAfterAbsolute",
                                                "dateBeforeAbsolute",
                                                "dateAfterPast",
                                                "dateBeforePast",
                                                "dateAfterFuture",
                                                "dateBeforeFuture",
                                            ],
                                        }),
                                        children: [],
                                    },
                                    {
                                        definition: Object.assign({
                                            key: "interactionsDatesEmailThreadsLastReceived",
                                            label: "interactionsDatesEmailThreadsLastReceived",
                                            type: "genericDate",
                                            parameters: {
                                                property:
                                                    "orgs____email_thread_dates_by_organization__date_last_email_received",
                                            },
                                            genericDataTypeId:
                                                _this.GENERIC_DATA_TYPES
                                                    .DATETIME.id,
                                            tests: [
                                                "isNull",
                                                "isNotNull",
                                                "dateEquals",
                                                "dateAfterAbsolute",
                                                "dateBeforeAbsolute",
                                                "dateAfterPast",
                                                "dateBeforePast",
                                                "dateAfterFuture",
                                                "dateBeforeFuture",
                                            ],
                                        }),
                                        children: [],
                                    },
                                ],
                            },
                            {
                                definition: Object.assign({
                                    key: "interactionsMostRecentConversation",
                                    label: "interactionsMostRecentConversation",
                                    type: "genericDate",
                                    parameters: {
                                        property:
                                            "orgs____interaction_dates_by_organization__conversations",
                                    },
                                    genericDataTypeId:
                                        _this.GENERIC_DATA_TYPES.DATETIME.id,
                                    tests: [
                                        "isNull",
                                        "isNotNull",
                                        "dateEquals",
                                        "dateAfterAbsolute",
                                        "dateBeforeAbsolute",
                                        "dateAfterPast",
                                        "dateBeforePast",
                                        "dateAfterFuture",
                                        "dateBeforeFuture",
                                    ],
                                }),
                                children: [],
                            },
                            {
                                definition: Object.assign({
                                    key: "interactionsMostRecentDeal",
                                    label: "interactionsMostRecentDeal",
                                    type: "genericDate",
                                    parameters: {
                                        property:
                                            "orgs____interaction_dates_by_organization__deal",
                                    },
                                    genericDataTypeId:
                                        _this.GENERIC_DATA_TYPES.DATETIME.id,
                                    tests: [
                                        "isNull",
                                        "isNotNull",
                                        "dateEquals",
                                        "dateAfterAbsolute",
                                        "dateBeforeAbsolute",
                                        "dateAfterPast",
                                        "dateBeforePast",
                                        "dateAfterFuture",
                                        "dateBeforeFuture",
                                    ],
                                }),
                                children: [],
                            },
                            {
                                definition: Object.assign({
                                    key: "interactionsMostRecentInvoice",
                                    label: "interactionsMostRecentInvoice",
                                    type: "genericDate",
                                    parameters: {
                                        property:
                                            "orgs____interaction_dates_by_organization__invoice",
                                    },
                                    genericDataTypeId:
                                        _this.GENERIC_DATA_TYPES.DATETIME.id,
                                    tests: [
                                        "isNull",
                                        "isNotNull",
                                        "dateEquals",
                                        "dateAfterAbsolute",
                                        "dateBeforeAbsolute",
                                        "dateAfterPast",
                                        "dateBeforePast",
                                        "dateAfterFuture",
                                        "dateBeforeFuture",
                                    ],
                                }),
                                children: [],
                            },
                            {
                                definition: Object.assign({
                                    key: "interactionsMostRecentTicket",
                                    label: "interactionsMostRecentTicket",
                                    type: "genericDate",
                                    parameters: {
                                        property:
                                            "orgs____interaction_dates_by_organization__ticket",
                                    },
                                    genericDataTypeId:
                                        _this.GENERIC_DATA_TYPES.DATETIME.id,
                                    tests: [
                                        "isNull",
                                        "isNotNull",
                                        "dateEquals",
                                        "dateAfterAbsolute",
                                        "dateBeforeAbsolute",
                                        "dateAfterPast",
                                        "dateBeforePast",
                                        "dateAfterFuture",
                                        "dateBeforeFuture",
                                    ],
                                }),
                                children: [],
                            },
                        ],
                    },
                ],
            },
            {
                definition: Object.assign({
                    label: "trackedMetrics",
                }),
                children: axios
                    .get(window.akita.urls.api + "/v1/metrics", {
                        params: {
                            per_page: 100,
                            q: ["entity_type_id[eq]=1"],
                        },
                    })
                    .then(function (response) {
                        var items = [];
                        response.data.data.map((metric) => {
                            items.push({
                                definition: Object.assign({
                                    key: "metric_" + metric.id,
                                    type: "statisticDelta",
                                    genericDataTypeId:
                                        _this.lookupGenericDataType(
                                            metric.data_type_id
                                        ),
                                    label: () => {
                                        return metric.name;
                                    },
                                    parameters: {
                                        typeId: 2,
                                        relatedId: metric.id,
                                    },
                                    tests: [
                                        "numberIncreasedGreaterThanOrEqualTo",
                                        "numberIncreasedGreaterThan",
                                        "numberIncreasedLessThanOrEqualTo",
                                        "numberIncreasedLessThan",
                                        "numberDecreasedGreaterThanOrEqualTo",
                                        "numberDecreasedGreaterThan",
                                        "numberDecreasedLessThanOrEqualTo",
                                        "numberDecreasedLessThan",
                                        "numberChangedBetween",
                                    ],
                                }),
                                children: [],
                            });
                        });
                        return items;
                    }),
            },
            {
                definition: Object.assign({
                    label: "segment",
                }),
                children: [
                    {
                        definition: Object.assign({
                            key: "segment",
                            label: "segmentInNotIn",
                            type: "segment",
                            dataTypeId: _this.GENERIC_DATA_TYPES.NUMBER.id,
                            tests: ["segmentIn", "segmentNotIn"],
                            propertiesLocation: "test",
                            properties: [
                                {
                                    name: "segment",
                                    label: () => {
                                        return $i18n.t(
                                            "entityFilters.properties.segmentName"
                                        );
                                    },
                                    type: "select",
                                    decorate: [
                                        {
                                            method: "remote",
                                            closure: "segment__remote",
                                        },
                                        {
                                            method: "validate",
                                            closure: "segment__validate",
                                        },
                                    ],
                                },
                            ],
                        }),
                        children: [],
                    },
                    {
                        definition: Object.assign({
                            key: "segment",
                            label: "segmentEverNeverIn",
                            type: "segment",
                            dataTypeId: _this.GENERIC_DATA_TYPES.NUMBER.id,
                            tests: ["segmentEverIn", "segmentNeverIn"],
                            propertiesLocation: "test",
                            properties: [
                                {
                                    name: "segment",
                                    label: () => {
                                        return $i18n.t(
                                            "entityFilters.properties.segmentName"
                                        );
                                    },
                                    type: "select",
                                    decorate: [
                                        {
                                            method: "remote",
                                            closure: "segment__remote",
                                        },
                                        {
                                            method: "validate",
                                            closure: "segment__validate",
                                        },
                                    ],
                                },
                            ],
                        }),
                        children: [],
                    },

                    {
                        definition: Object.assign({
                            key: "segmentFirstEnterRecordedAt",
                            label: "segmentFirstEnterRecordedAt",
                            type: "segmentDate",
                            genericDataTypeId:
                                _this.GENERIC_DATA_TYPES.DATETIME.id,
                            tests: [
                                "isNull",
                                "isNotNull",
                                "dateEquals",
                                "dateAfterAbsolute",
                                "dateBeforeAbsolute",
                                "dateAfterPast",
                                "dateBeforePast",
                            ],
                            parameters: {
                                property: "first_enter_recorded_at",
                            },
                            decorate: [
                                {
                                    method: "summaryLabelOverride",
                                    closure:
                                        "segmentFirstEnter__summaryLabelOverride",
                                },
                            ],
                            properties: [
                                {
                                    name: "segment",
                                    label: () => {
                                        return $i18n.t(
                                            "entityFilters.properties.segmentName"
                                        );
                                    },
                                    type: "select",
                                    decorate: [
                                        {
                                            method: "remote",
                                            closure: "segment__remote",
                                        },
                                        {
                                            method: "validate",
                                            closure: "segment__validate",
                                        },
                                    ],
                                },
                            ],
                        }),
                        children: [],
                    },
                    {
                        definition: Object.assign({
                            key: "segmentLastEnterRecordedAt",
                            label: "segmentLastEnterRecordedAt",
                            type: "segmentDate",
                            genericDataTypeId:
                                _this.GENERIC_DATA_TYPES.DATETIME.id,
                            tests: [
                                "isNull",
                                "isNotNull",
                                "dateEquals",
                                "dateAfterAbsolute",
                                "dateBeforeAbsolute",
                                "dateAfterPast",
                                "dateBeforePast",
                            ],
                            parameters: {
                                property: "last_enter_recorded_at",
                            },
                            decorate: [
                                {
                                    method: "summaryLabelOverride",
                                    closure:
                                        "segmentLastEnter__summaryLabelOverride",
                                },
                            ],
                            properties: [
                                {
                                    name: "segment",
                                    label: () => {
                                        return $i18n.t(
                                            "entityFilters.properties.segmentName"
                                        );
                                    },
                                    type: "select",
                                    decorate: [
                                        {
                                            method: "remote",
                                            closure: "segment__remote",
                                        },
                                        {
                                            method: "validate",
                                            closure: "segment__validate",
                                        },
                                    ],
                                },
                            ],
                        }),
                        children: [],
                    },
                    {
                        definition: Object.assign({
                            key: "segmentFirstExitRecordedAt",
                            label: "segmentFirstExitRecordedAt",
                            type: "segmentDate",
                            genericDataTypeId:
                                _this.GENERIC_DATA_TYPES.DATETIME.id,
                            tests: [
                                "isNull",
                                "isNotNull",
                                "dateEquals",
                                "dateAfterAbsolute",
                                "dateBeforeAbsolute",
                                "dateAfterPast",
                                "dateBeforePast",
                            ],
                            parameters: {
                                property: "first_exit_recorded_at",
                            },
                            decorate: [
                                {
                                    method: "summaryLabelOverride",
                                    closure:
                                        "segmentFirstExit__summaryLabelOverride",
                                },
                            ],
                            properties: [
                                {
                                    name: "segment",
                                    label: () => {
                                        return $i18n.t(
                                            "entityFilters.properties.segmentName"
                                        );
                                    },
                                    type: "select",
                                    decorate: [
                                        {
                                            method: "remote",
                                            closure: "segment__remote",
                                        },
                                        {
                                            method: "validate",
                                            closure: "segment__validate",
                                        },
                                    ],
                                },
                            ],
                        }),
                        children: [],
                    },
                    {
                        definition: Object.assign({
                            key: "segmentLastExitRecordedAt",
                            label: "segmentLastExitRecordedAt",
                            type: "segmentDate",
                            genericDataTypeId:
                                _this.GENERIC_DATA_TYPES.DATETIME.id,
                            tests: [
                                "isNull",
                                "isNotNull",
                                "dateEquals",
                                "dateAfterAbsolute",
                                "dateBeforeAbsolute",
                                "dateAfterPast",
                                "dateBeforePast",
                            ],
                            parameters: {
                                property: "last_exit_recorded_at",
                            },
                            decorate: [
                                {
                                    method: "summaryLabelOverride",
                                    closure:
                                        "segmentLastExit__summaryLabelOverride",
                                },
                            ],
                            properties: [
                                {
                                    name: "segment",
                                    label: () => {
                                        return $i18n.t(
                                            "entityFilters.properties.segmentName"
                                        );
                                    },
                                    type: "select",
                                    decorate: [
                                        {
                                            method: "remote",
                                            closure: "segment__remote",
                                        },
                                        {
                                            method: "validate",
                                            closure: "segment__validate",
                                        },
                                    ],
                                },
                            ],
                        }),
                        children: [],
                    },
                ],
            },
            {
                definition: Object.assign({
                    key: "tags",
                    label: "tags",
                    type: "segment",
                    dataTypeId: _this.GENERIC_DATA_TYPES.NUMBER.id,
                    labelProperty: "segmentId",
                    tests: ["tagIn", "tagNotIn"],
                    propertiesLocation: "test",
                    properties: [
                        {
                            name: "segment",
                            label: () => {
                                return $i18n.t(
                                    "entityFilters.properties.tagName"
                                );
                            },
                            type: "select",
                            decorate: [
                                {
                                    method: "remote",
                                    closure: "tag__remote",
                                },
                                {
                                    method: "validate",
                                    closure: "tag__validate",
                                },
                            ],
                        },
                    ],
                }),
                children: [],
            },
            {
                definition: Object.assign({
                    key: "playbook",
                    label: "playbook",
                    type: "playbook",
                    dataTypeId: _this.GENERIC_DATA_TYPES.NUMBER.id,
                    labelProperty: "playbookId",
                    tests: [
                        "playbookCompleted",
                        "playbookCompletedSuccessfully",
                        "playbookNotCompleted",
                        "playbookNotCompletedSuccessfully",
                        "playbookIn",
                        "playbookEverIn",
                        "playbookNotIn",
                        "playbookNeverIn",
                    ],
                    propertiesLocation: "test",
                    properties: [
                        {
                            name: "playbook",
                            label: () => {
                                return $i18n.t(
                                    "entityFilters.properties.playbookName"
                                );
                            },
                            type: "select",
                            decorate: [
                                {
                                    method: "remote",
                                    closure: "playbook__remote",
                                },
                                {
                                    method: "validate",
                                    closure: "playbook__validate",
                                },
                            ],
                        },
                    ],
                }),
                children: [],
            },
            // {
            //     label: "interactionCounts",
            //     children: axios
            //         .get(window.akita.urls.api + "/v1/integrations", {
            //             params: {
            //                 include: ["provider"],
            //                 counts: "true",
            //             },
            //         })
            //         .then((response) => {
            //             var items = [];
            //             response.data.data.map((integration) => {
            //                 items.push({
            //                     label:
            //                         integration.label ||
            //                         integration.provider.name,
            //                     children: axios
            //                         .get(
            //                             window.akita.urls.api +
            //                                 "/v1/integration-objects",
            //                             {
            //                                 params: {
            //                                     q: [
            //                                         "provider_id[eq]=" +
            //                                             integration.provider_id,
            //                                     ],
            //                                     include: ["interactionType"],
            //                                 },
            //                             }
            //                         )
            //                         .then((response) => {
            //                             var items = [];
            //                             var filtered = _.filter(
            //                                 response.data.data,
            //                                 (object) => {
            //                                     return (
            //                                         object.interaction_type_id !==
            //                                         1
            //                                     ); // NOT AN ORGANIZATION
            //                                 }
            //                             );
            //                             filtered.map((integrationObject) => {
            //                                 items.push({
            //                                     definition: Object.assign({
            //                                         key:
            //                                             integrationObject.provider_id +
            //                                             "_" +
            //                                             integrationObject.adapter_code +
            //                                             " " +
            //                                             integrationObject.interaction_type,
            //                                         type: "genericNumeric",
            //                                         label:
            //                                             integrationObject
            //                                                 .interaction_type
            //                                                 .name,
            //                                         subfilters: true,
            //                                         tests: [
            //                                             "numberGreaterThan",
            //                                             "numberGreaterThanOrEqualTo",
            //                                             "numberLessThan",
            //                                             "numberLessThanOrEqualTo",
            //                                             "numberBetween",
            //                                             "numberEquals",
            //                                         ],
            //                                     }),
            //                                     children: [],
            //                                 });
            //                             });
            //                             return items;
            //                         }),
            //                 });
            //             });
            //             return items;
            //         }),
            // },
        ];

        let methods = {
            pagePath__remote: function () {
                let _this = this;
                if (this.value !== "") {
                    _this.remoteLoading = true;
                    axios
                        .get(window.akita.urls.api + "/v1/page-view-lookup", {
                            params: {
                                path: this.value,
                            },
                        })
                        .then(function (response) {
                            _this.remoteLoading = false;
                            var items = [];
                            response.data.data.map((pageView) => {
                                items.push(pageView.path);
                            });
                            _this.options = items.sort();
                        });
                } else {
                    this.options = [];
                }
            },
            pageName__remote: function () {
                let _this = this;
                if (_this.value !== "") {
                    _this.remoteLoading = true;
                    axios
                        .get(window.akita.urls.api + "/v1/page-view-lookup", {
                            params: {
                                name: _this.value,
                            },
                        })
                        .then(function (response) {
                            _this.remoteLoading = false;

                            var items = [];
                            response.data.data.map((pageView) => {
                                items.push(pageView.name);
                            });
                            _this.options = items.sort();
                        });
                } else {
                    _this.options = [];
                }
            },
            event__remote: function () {
                let _this = this;
                if (_this.value !== "") {
                    _this.remoteLoading = true;
                    axios
                        .get(window.akita.urls.api + "/v1/event-names", {
                            params: {
                                per_page: 1000,
                                per_page: 100,
                                q: ["name[like]=" + _this.value],
                            },
                        })
                        .then(function (response) {
                            _this.remoteLoading = false;

                            var items = [];
                            response.data.data.map((item) => {
                                items.push(item.event);
                            });
                            _this.options = items.sort();
                        });
                } else {
                    _this.options = [];
                }
            },
            playbook__remote: function (query) {
                let _this = this;
                if (query !== "") {
                    _this.remoteLoading = true;
                    axios
                        .get(window.akita.urls.api + "/v1/playbooks", {
                            params: {
                                q: ["entity_type_id[eq]=1"],
                                per_page: 100,
                            },
                        })
                        .then(function (response) {
                            _this.remoteLoading = false;

                            var items = [];
                            response.data.data.map((item) => {
                                items.push({
                                    value: item.id,
                                    label: item.name,
                                });
                            });
                            _this.options = items.sort(function (a, b) {
                                if (a.label > b.label) {
                                    return -1;
                                }
                                if (b.label > a.label) {
                                    return 1;
                                }
                                return 0;
                            });
                        });
                } else {
                    _this.options = [];
                }
            },
            segment__remote: function (query) {
                let _this = this;
                if (query !== "") {
                    _this.remoteLoading = true;
                    axios
                        .get(window.akita.urls.api + "/v1/segments", {
                            params: {
                                q: [
                                    "entity_type_id[eq]=1",
                                    "type_id[in]=1,2,3,7,8,9",
                                    "name[like]=" + query,
                                ],
                                per_page: 100,
                            },
                        })
                        .then(function (response) {
                            _this.remoteLoading = false;
                            var items = [];
                            response.data.data.map((item) => {
                                items.push({
                                    value: item.id,
                                    label: item.name,
                                });
                            });
                            _this.options = items.sort(function (a, b) {
                                if (a.label > b.label) {
                                    return -1;
                                }
                                if (b.label > a.label) {
                                    return 1;
                                }
                                return 0;
                            });
                        });
                } else {
                    _this.options = [];
                }
            },
            tag__remote: function (query) {
                let _this = this;
                if (query !== "") {
                    _this.remoteLoading = true;
                    axios
                        .get(window.akita.urls.api + "/v1/segments", {
                            params: {
                                q: [
                                    "entity_type_id[eq]=1",
                                    "type_id[in]=1,7",
                                    "tag_enabled[eq]=true",
                                    "name[like]=" + query,
                                ],
                                per_page: 100,
                            },
                        })
                        .then(function (response) {
                            _this.remoteLoading = false;
                            var items = [];
                            response.data.data.map((item) => {
                                items.push({
                                    value: item.id,
                                    label: item.name,
                                });
                            });
                            _this.options = items.sort(function (a, b) {
                                if (a.label > b.label) {
                                    return -1;
                                }
                                if (b.label > a.label) {
                                    return 1;
                                }
                                return 0;
                            });
                        });
                } else {
                    _this.options = [];
                }
            },
            tag__validate: function (filter, $i18n) {
                // value is required
                if ("" == _.get(filter, "parameters.segment", "")) {
                    return $i18n
                        ? $i18n.t("entityFilters.validation.requiredMissing", {
                              value: $i18n.t(
                                  "entityFilters.properties.tagName"
                              ),
                          })
                        : false;
                }

                return true;
            },
            event__validate: function (filter, $i18n) {
                // value is required
                if ("" == _.get(filter, "parameters.event", "")) {
                    return $i18n
                        ? $i18n.t("entityFilters.validation.requiredMissing", {
                              value: $i18n.t(
                                  "entityFilters.properties.eventName"
                              ),
                          })
                        : false;
                }

                return true;
            },
            segment__validate: function (filter, $i18n) {
                // value is required
                if ("" == _.get(filter, "parameters.segment", "")) {
                    return $i18n
                        ? $i18n.t("entityFilters.validation.requiredMissing", {
                              value: $i18n.t(
                                  "entityFilters.properties.segmentName"
                              ),
                          })
                        : false;
                }

                return true;
            },
            playbook__validate: function (filter, $i18n) {
                // value is required
                if ("" == _.get(filter, "parameters.playbook", "")) {
                    return $i18n
                        ? $i18n.t("entityFilters.validation.requiredMissing", {
                              value: $i18n.t(
                                  "entityFilters.properties.playbook"
                              ),
                          })
                        : false;
                }

                return true;
            },
            pagePath__validate: function (filter, $i18n) {
                // value is required
                if ("" == _.get(filter, "parameters.pagePath", "")) {
                    return $i18n
                        ? $i18n.t("entityFilters.validation.requiredMissing", {
                              value: $i18n.t(
                                  "entityFilters.properties.pageViewsPagePath"
                              ),
                          })
                        : false;
                }

                return true;
            },
            pageName__validate: function (filter, $i18n) {
                // value is required
                if ("" == _.get(filter, "parameters.pageName", "")) {
                    return $i18n
                        ? $i18n.t("entityFilters.validation.requiredMissing", {
                              value: $i18n.t(
                                  "entityFilters.properties.pageViewsPageName"
                              ),
                          })
                        : false;
                }

                return true;
            },
            segmentFirstEnter__summaryLabelOverride: function (filter) {
                return (
                    "Date first joined '" +
                    _.get(filter, "parameters.segment.label", "Unknown") +
                    "'"
                );
            },
            segmentFirstExit__summaryLabelOverride: function (filter) {
                return (
                    "Date first left '" +
                    _.get(filter, "parameters.segment.label", "Unknown") +
                    "'"
                );
            },
            segmentLastEnter__summaryLabelOverride: function (filter) {
                return (
                    "Date most recently joined '" +
                    _.get(filter, "parameters.segment.label", "Unknown") +
                    "'"
                );
            },
            segmentLastExit__summaryLabelOverride: function (filter) {
                return (
                    "Date most recently left '" +
                    _.get(filter, "parameters.segment.label", "Unknown") +
                    "'"
                );
            },
        };

        if (this.fromCache) {
            let rawChildren = _.cloneDeep(this.children);
            let childrenFromCache = this.fromCache.children;

            /**
             * This adds the "decoration" property from the raw/unresolved
             * definition in this file to the data serialized and cached in
             * local storage so the methods can be applied below
             */
            this.children = this.addDecorateDefinition(
                rawChildren,
                childrenFromCache
            );
        } else {
            let result = await this.resolve();
            result.translate($i18n, $store);
            this.children = result.children;
        }

        /**
         * Add back in javascript fundtions that cannot be serialized (remote)
         */
        this.decorate(this.children, methods);
        this.initialized = true;
    };
}

export default AccountFilters;
